import {
	Dialog,
	Toolbar
} from "client/Components";
import { isNil } from "client/Misc/utils";
import { ReactElement, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { FolderSelector, Mailbox } from "../FolderSelector/FolderSelector";
import { SelectedFolder } from "../FolderSelector/useFolderChildren";
import { MediaQueryKind, useDataInputDialog, useWorkspaceMediaQuery } from "client/Hooks";

export type SelectFolderInMailboxDialogProps = {
	mailbox: Mailbox;
	onSelect(folder: SelectedFolder & { id: string; }): void;
	onClose(): void;
};

export const SelectFolderInMailboxDialog = (props: SelectFolderInMailboxDialogProps): ReactElement => {
	const {
		mailbox,
		onSelect,
		onClose
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const [currentFolder, setCurrentFolder] = useState<SelectedFolder>();

	const selectHandler = (): void => {
		if (isNil(currentFolder) || isNil(currentFolder.id)) {
			throw new Error("You should not be able to click the button");
		}

		const {
			id,
			name,
			smtpAddress
		} = currentFolder;

		onSelect({
			id,
			name,
			smtpAddress
		});

		onClose();
	};

	return (
		<Dialog
			trackingId="select-email-folder-dialog"
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_SelectAFolder")}
				onClose={onClose}
				hasBorder
			/>

			<Dialog.Body
				goesEdgeToEdge
			>
				<FolderSelector
					mailbox={mailbox}
					onChange={setCurrentFolder}
				/>
			</Dialog.Body>

			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Select")}
						onClick={selectHandler}
						isDisabled={isNil(currentFolder?.id)}
						trackingId="select"
					/>

					{!isMobile && (
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					)}
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SelectFolderInMailboxDialog.displayName = "SelectFolderInMailboxDialog";

export type SelectFolderInMailboxDialogData = Omit<SelectFolderInMailboxDialogProps, "onSelect" | "onClose">;

export function useSelectFolderInMailboxDialog() {
	return useDataInputDialog<SelectFolderInMailboxDialogData, SelectedFolder & { id: string; }>(
		(data, onClose, onSelect) => (
			<SelectFolderInMailboxDialog
				{...data}
				onClose={onClose}
				onSelect={onSelect}
			/>
		)
	);
}